<script setup lang="ts">
import { computed, CSSProperties, inject, onUnmounted, PropType, Ref, ref, watch } from 'vue';
import { ChevronDownIcon, FlagIcon } from 'tir-style-system/icons/solid';
import { DragModel, TirPmDraggable } from '../../tir-pm-kanban';
import { EllipsisHorizontalIcon, PlusIcon } from 'tir-style-system/icons/solid';
import { GroupedTaskItemModel, TaskModel } from '../../tasks-kanban/models';
import { TasksKanbanColumnIndicator } from '../../tasks-kanban/components';
import { TaskDrawerFormDataModel } from '../../task-forms';
import { TaskParameter, TaskParameterTypeEnum } from '@/components';
import { ExtensionModel } from '../../edit-queue-modal/models';
import { HeaderItemModel, ListItemModel } from '../models';
import { TirPmIconView, TirPmTooltipElement } from 'tir-components';
import { ProjectModel } from '@/models';
import { TaskViewElementEnum } from '../enums';
import {
  Bars3BottomLeftIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  EstimateTimeIcon,
  EyeIcon,
  PlayCircleIcon,
  RectangleStackIcon,
  TimeSpentIcon,
  UserCircleIcon,
  UserIcon,
} from 'tir-style-system/icons/outline';
import { TasksKanbanGrouppingTypeEnum } from '@/components/tasks-kanban/enums';

/**Параметры */
const props = defineProps({
  /**Данные о группе */
  group: { type: Object as PropType<GroupedTaskItemModel> },
  /**Размер имени задачи */
  nameWidth: { type: String },
  /**Размеры колонок списка */
  listColumns: { type: Array as PropType<ListItemModel[]> },
  /**Текущий проект */
  currentProject: { type: Array as PropType<ProjectModel[]> },
  /**Тип группировки */
  grouppingType: { type: Number as PropType<TasksKanbanGrouppingTypeEnum> },
  /**Активная сортировка */
  activeSortType: { type: String as PropType<TaskViewElementEnum> },
});
/**События */
const emit = defineEmits<{
  /**Событие для создания задачи */
  create: [form: TaskDrawerFormDataModel];
  /**Событие дропа на верхний уровень */
  dragComponentOnDrop: [data: DragModel<GroupedTaskItemModel>];
  /**Событие сортировки списка */
  sortingList: [type: TaskViewElementEnum];
}>();

/**Перетаскиваемый элемент */
const currentDragging = inject<Ref<DragModel<TaskModel>>>('currentDragging');
/**Элемент над которым находится перетаскиваемый объект */
const overDragging = inject<Ref<DragModel<GroupedTaskItemModel>>>('overDragging');
/**Модель для области дропа на верхний уровень */
const dropAreaData = ref(
  new DragModel({
    AllowDrag: false,
    AllowDrop: true,
    Data: props.group,
  }),
);
/**Раскрыт ли список */
const visible = ref(false);
/**Сортируемые элементы */
const sortableTypes = [
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.Difficulty,
  TaskViewElementEnum.Deadline,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
];

/**Кол-во элементов */
const itemsCount = computed(() => props.group?.Items?.length || 0);
/**Заголовки списка */
const headers = computed(() => {
  const header = props.listColumns.map(
    (x, i) =>
      new HeaderItemModel({
        Name: x.Name,
        Type: x.Type,
        Style: {
          width: props.listColumns[i].Width,
        },
        Icon: getIconTypeElement(x.Type),
        StateSort: false,
      }),
  );
  return header;
});
/**Стили элемента заголовка */
const headersStyle = computed(() => ({ left: `calc(${props.nameWidth} + 2.5rem)` }));

/**Отслеживание типа группировки */
watch(
  () => props.grouppingType,
  () => {
    visible.value = props.grouppingType === TasksKanbanGrouppingTypeEnum.None;
  },
  { immediate: true },
);

/**Переключить видимость списка */
const toggleVisible = () => {
  if (props.grouppingType === TasksKanbanGrouppingTypeEnum.None) return;
  visible.value = !visible.value;
};
/**Получить иконку типа элемента шапки */
const getIconTypeElement = (type: TaskViewElementEnum) => {
  switch (type) {
    case TaskViewElementEnum.Author:
      return UserIcon;
    case TaskViewElementEnum.Performers:
      return UserCircleIcon;
    case TaskViewElementEnum.Observers:
      return EyeIcon;
    case TaskViewElementEnum.Difficulty:
      return ChartBarIcon;
    case TaskViewElementEnum.Priority:
      return FlagIcon;
    case TaskViewElementEnum.Type:
      return RectangleStackIcon;
    case TaskViewElementEnum.Deadline:
      return ClockIcon;
    case TaskViewElementEnum.DateCreate:
      return CalendarDaysIcon;
    case TaskViewElementEnum.EstimateTime:
      return EstimateTimeIcon;
    case TaskViewElementEnum.WorkTime:
      return PlayCircleIcon;
    default:
      return Bars3BottomLeftIcon;
  }
};
/**Сброс элемента на верхний уровень */
const onAreaDrop = () => {
  emit('dragComponentOnDrop', dropAreaData.value);
  setTimeout(() => {
    overDragging.value = undefined;
    currentDragging.value = undefined;
  });
};
/**Наведение на область сброса */
const onAreaOver = (isOver: boolean) => {
  overDragging.value = isOver ? dropAreaData.value : undefined;
};
/**Сортировка списка */
const sortingList = (elem: HeaderItemModel) => {
  if (sortableTypes.includes(elem.Type)) emit('sortingList', elem.Type);
};
/**Отправка события для открытия окна с созданием задачи */
const createTask = () => {
  emit(
    'create',
    new TaskDrawerFormDataModel({
      StatusId: props.group?.Status?.Id,
      PriorityId: props.group?.Priority?.Id,
      TypeId: props.group?.Type?.Id,
      PerformerIds: props.group?.Performer?.Id ? [props.group.Performer.Id] : [],
      Tags: props.group?.Tag?.Id ? [props.group.Tag.Id] : [],
    }),
  );
};
/**Данные о статусе/приоритете/тэге */
const badgeData = computed(() => {
  const data = props.group.Status || props.group.Priority || props.group.Tag;
  return new ExtensionModel({
    Name: data.Name,
    Color: data.Color,
  });
});

/**Тип бейджа */
const badgeType = computed(() => {
  if (props.group.Status) return TaskParameterTypeEnum.Status;
  if (props.group.Priority) return TaskParameterTypeEnum.Priority;
  return;
});
</script>
<template>
  <div class="tir-pm-task-group" :class="{ 'is-open': visible }">
    <div class="tir-pm-task-group_header">
      <div class="label" @click="toggleVisible">
        <div class="label_sticky">
          <div class="tir-pm-task-group_icon-wrapper chevron">
            <ChevronDownIcon />
          </div>
          <TaskParameter
            v-if="badgeType != undefined"
            preview
            :data="badgeData"
            :type="badgeType"
          />
          <TasksKanbanColumnIndicator v-else :grouping="group" />
          <div v-for="project in props.currentProject" class="curtain__info">
            <TirPmIconView class="curtain__icon" :size="24" :isBordered="false" isRounded>
              <template #mainSlot>
                <img :src="project.ImageUrl" alt="" />
              </template>
            </TirPmIconView>
            <p class="curtain__title">{{ project.Name }}</p>
            <b>({{ group.Items?.length || 0 }})</b>
          </div>
          <div class="tir-pm-task-group_icon-wrapper count">
            <div>
              {{ group.Items?.length || 0 }}
            </div>
          </div>
          <div class="tir-pm-task-group_controls">
            <div class="tir-pm-task-group_controls">
              <div class="tir-pm-task-group_icon-wrapper" @click.stop="createTask">
                <PlusIcon />
              </div>
            </div>
          </div>
        </div>
        <div v-if="visible && itemsCount" class="tir-pm-task-group_headers" :style="headersStyle">
          <div v-for="header in headers" :style="header.Style as CSSProperties">
            <TirPmTooltipElement
              :isDisabled="header.Type === TaskViewElementEnum.Status"
              width="max-content"
              class="tir-pm-task-group_hint"
            >
              <div
                v-if="header.Type === TaskViewElementEnum.Status"
                class="tir-pm-task-group_header--title"
              >
                {{ header.Name }}
              </div>
              <component
                :is="header.Icon"
                class="tir-pm-task-group_icon"
                :class="{ 'tir-pm-task-group_icon--active': activeSortType === header.Type }"
                @click.stop="sortingList(header)"
              />
              <template #content>
                {{ header.Name }}
              </template>
            </TirPmTooltipElement>
          </div>
        </div>
      </div>
    </div>
    <transition name="scale_label_content">
      <div class="field" :class="{ open: !!currentDragging }" v-show="visible">
        <TirPmDraggable
          :dragModel="dropAreaData"
          @drop="onAreaDrop"
          @over="onAreaOver"
          class="field-drop_area"
          :class="{
            open: !!currentDragging,
            over: !!currentDragging && overDragging?.Data.Id === dropAreaData.Data.Id,
          }"
        />
        <slot />
      </div>
    </transition>
  </div>
</template>
<style lang="scss">
.curtain {
  &__info {
    display: flex;
    align-items: center;
  }
  &__icon {
    margin-right: 0.25rem;
  }
  &__title {
    margin-right: 0.4rem;
  }
}

.is-open-tasks-overview {
  .label {
    background: var(--neutral-translucent-10, #272d371a);
  }
}

.tir-pm-task-group {
  display: flex;
  flex-direction: column;
  min-width: max-content;
  gap: 0.5rem;
  margin-bottom: 2rem;
  &_hint {
    height: 1rem !important;
  }
  &_icon {
    width: 1rem;
    height: 1rem;
    color: var(--text-secondary, #686c73);
    &--active {
      color: var(--text-info, #2a77ef);
    }
  }
  &_header {
    position: sticky;
    z-index: 2;
    background-color: var(--bg-base);
    top: 0;
    &--title {
      margin-right: 0.25rem;
    }
  }
  .label {
    position: relative;
    display: flex;
    transition: 0.1s ease;
    cursor: pointer;
    border-radius: 0.375rem;
    user-select: none;

    &_sticky {
      border-radius: 0.375rem;
      position: sticky;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;
      height: 2rem;
      gap: 0.25rem;
      z-index: 1;
      background-color: var(--bg-base);
      width: 30rem;
    }
    &:hover {
      background: var(--neutral-translucent-5);
      .label_sticky {
        background: var(--neutral-05);
      }
    }
  }

  &_headers {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    color: var(--text-tertiary);
    font-size: 0.75rem;
  }

  &_controls {
    display: flex;
    gap: 0.25rem;
  }

  &_icon-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 1.5rem;
    width: 1.5rem;
    transition: 0.2s linear;
    & > svg {
      height: 1rem;
      width: 1rem;
    }
    &.count {
      margin-right: 0.5rem;
      color: var(--text-tertiary);
    }
  }

  &.is-open {
    .tir-pm-task-group_icon-wrapper.chevron {
      transform: rotate(180deg);
    }
  }

  .field {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &.open {
      padding-left: 3.375rem;
      transition: 0.5s ease;
    }
    &-drop_area {
      position: absolute;
      left: 0;
      top: 0;
      width: 0;
      height: 100%;
      border-radius: 0.5rem;
      background-color: var(--neutral-0);
      z-index: 1;

      &.open {
        border: 0.125rem solid transparent;
        width: 3.125rem;
        animation: border-blink 1s ease infinite;
        transition: 0.5s ease;
        border-color: #3a7cc2;

        @keyframes border-blink {
          0% {
            border-color: #3a7cc2;
          }
          50% {
            border-color: transparent;
          }
        }
      }
      &.over {
        animation: none;
        border-color: #3a7cc2;
      }
    }
  }
}

.scale_label_content-enter-active,
.scale_label_content-leave-active {
  transition-duration: var(--tir-label-transition_duration);
  transition-timing-function: var(--tir-label-transition_timing_function);
  transition-property: opacity transform;
  transform-origin: top;
}

.scale_label_content-enter-from,
.scale_label_content-leave-to {
  transform: scaleY(0);
  transform-origin: top;
  opacity: 0;
}
</style>
